/**
 * usePermissionRefresh - B2C Merchant Portal
 *
 * Automatically refreshes the permission cache when:
 *  1. The window regains focus after the cache TTL has expired
 *  2. The document becomes visible after being hidden past TTL
 *
 * Place this hook once at the app shell level (e.g., AppLayout or PrivateRoute).
 * It is safe to call multiple times - refresh is debounced.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadPermissions, selectIsPermissionStale, REFRESH_DEBOUNCE_MS } from '../redux/slices/permissionsSlice';

export default function usePermissionRefresh() {
  const dispatch = useDispatch();
  const status = useSelector((s) => s.permissions.status);
  const isStale = useSelector(selectIsPermissionStale);
  const lastRefreshRef = useRef(0);

  const tryRefresh = useCallback(() => {
    const now = Date.now();
    // Guard: don't refresh if already loading, or if recently refreshed
    if (status === 'loading') return;
    if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) return;
    if (!isStale) return;

    lastRefreshRef.current = now;
    dispatch(loadPermissions());
  }, [dispatch, status, isStale]);

  useEffect(() => {
    const onFocus = () => tryRefresh();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tryRefresh();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [tryRefresh]);
}
